import { useCallback, useRef, useState } from 'react';

type CreditStatus = 'OK' | 'NOK' | 'ERROR';

/**
 * Payload accepted by the credit-indication contact endpoint.
 *
 * `name` and `loanAmount` are optional because the B2B contact form variant
 * (contactMethod=api) does not collect them — the endpoint tolerates their
 * absence. The full multi-step credit check still passes them.
 */
export type SubmitContactPayload = {
    name?: string;
    email: string;
    telephone: string;
    loanAmount?: number;
    source?: string;
    message?: string;
    annualRevenue?: string;
    // The B2B contact form (contactMethod=api) can attach extra fields under
    // admin-configured camelCased API labels. The endpoint tolerates extra keys.
    [key: string]: string | number | undefined;
};

type ContactFieldErrors = {
    email?: string[];
    telephone?: string[];
    name?: string[];
    _form?: string[];
};

export type CompanyCreditCheckFields = {
    orgNumber: string;
    loanAmount: string;
    name: string;
    email: string;
    telephone: string;
    message?: string;
    annualRevenue?: string;
};

type UseCompanyCreditCheckOptions = {
    enableRecaptcha?: boolean;
    creditIndicationApiUrl?: string;
};

export const useCompanyCreditCheck = ({ enableRecaptcha = true, creditIndicationApiUrl }: UseCompanyCreditCheckOptions = {}) => {
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [fields, setFields] = useState<CompanyCreditCheckFields>({
        orgNumber: '',
        loanAmount: '',
        name: '',
        email: '',
        telephone: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [creditStatus, setCreditStatus] = useState<CreditStatus | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [contactError, setContactError] = useState<string | null>(null);
    const [contactFieldErrors, setContactFieldErrors] = useState<ContactFieldErrors>({});
    const [isSubmittingContact, setIsSubmittingContact] = useState(false);

    const abortControllerRef = useRef<AbortController | null>(null);

    const updateField = useCallback((updates: Partial<CompanyCreditCheckFields>) => {
        setFields(prev => ({ ...prev, ...updates }));
    }, []);

    const normalizeOrgNumber = (orgNumber: string): string => {
        return orgNumber.replace(/[^0-9]/g, '');
    };

    const checkCredit = useCallback(
        async (orgNumber: string, recaptchaToken?: string): Promise<CreditStatus | null> => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const controller = new AbortController();
            abortControllerRef.current = controller;

            setIsLoading(true);
            setError(null);
            setCreditStatus(null);

            const normalizedOrg = normalizeOrgNumber(orgNumber);

            // When captcha is disabled, call the Resurs API directly to avoid rate limiting on the backend proxy
            const url = enableRecaptcha ? `/api/credit-indication/${normalizedOrg}` : `${creditIndicationApiUrl}/credit-indication/${normalizedOrg}`;

            try {
                const response = await fetch(url, {
                    signal: controller.signal,
                    headers: {
                        ...(recaptchaToken && { 'x-recaptcha-token': JSON.stringify(recaptchaToken) }),
                    },
                });

                if (!response.ok) {
                    setCreditStatus('ERROR');
                    setStep(2);
                    return 'ERROR';
                }

                const data = (await response.json()) as { status: CreditStatus };
                setCreditStatus(data.status);
                setStep(2);
                return data.status;
            } catch (err) {
                if (err instanceof DOMException && err.name === 'AbortError') {
                    return null;
                }
                setCreditStatus('ERROR');
                setStep(2);
                return 'ERROR';
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        },
        [enableRecaptcha, creditIndicationApiUrl]
    );

    const submitContact = useCallback(
        async (orgNumber: string, contactData: SubmitContactPayload, recaptchaToken?: string): Promise<'OK' | 'ERROR' | 'VALIDATION_ERROR'> => {
            setIsSubmittingContact(true);
            setContactError(null);
            setContactFieldErrors({});

            const normalizedOrg = normalizeOrgNumber(orgNumber);

            // When captcha is disabled, call the Resurs API directly to avoid rate limiting on the backend proxy
            const url = enableRecaptcha
                ? `/api/credit-indication/${normalizedOrg}/contact`
                : `${creditIndicationApiUrl}/credit-indication/${normalizedOrg}/contact`;

            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(recaptchaToken && { 'x-recaptcha-token': JSON.stringify(recaptchaToken) }),
                    },
                    body: JSON.stringify(contactData),
                });

                if (response.status === 400) {
                    const data = (await response.json()) as { errors: ContactFieldErrors };
                    setContactFieldErrors(data.errors || {});
                    return 'VALIDATION_ERROR';
                }

                const data = (await response.json()) as { status: 'OK' | 'ERROR' };

                if (data.status === 'OK') {
                    setStep(3);
                    return 'OK';
                }

                setContactError('ERROR');
                return 'ERROR';
            } catch {
                setContactError('ERROR');
                return 'ERROR';
            } finally {
                setIsSubmittingContact(false);
            }
        },
        [enableRecaptcha, creditIndicationApiUrl]
    );

    const resetForm = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        setStep(1);
        setFields({
            orgNumber: '',
            loanAmount: '',
            name: '',
            email: '',
            telephone: '',
        });
        setCreditStatus(null);
        setError(null);
        setContactError(null);
        setContactFieldErrors({});
        setIsLoading(false);
        setIsSubmittingContact(false);
    }, []);

    return {
        step,
        fields,
        isLoading,
        creditStatus,
        error,
        contactError,
        contactFieldErrors,
        isSubmittingContact,
        updateField,
        checkCredit,
        submitContact,
        resetForm,
        _dev: {
            setStep,
            setIsLoading,
            setCreditStatus,
            setContactError,
            setIsSubmittingContact,
        },
    };
};
